import { View, Text, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  Mail,
  Shield,
  ChevronRight,
  LogOut,
  Moon,
  Lock,
  Bell,
  HelpCircle,
  FileText,
  Info,
  Pencil,
} from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useThemeStore } from '@/lib/state/theme-store';
import { useAuth } from '@/lib/supabase';
import { useProfileStore } from '@/lib/state/profile-store';
import { useInverioStore } from '@/lib/state/inverio-store';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { useAdminRole, ADMIN_ROLE_LABELS } from '@/lib/admin';
import { useAlert } from '@/components/AlertDialog';
import { hapticLight } from '@/lib/haptics';
import { isWeb } from '@/lib/platform';
import { AppLayout } from '@/components/AppLayout';
import AnimatedPressable from '@/components/AnimatedPressable';
import { reportError } from '@/lib/errors';
import { track } from '@/lib/analytics';

// ── Menu row helper ──

function MenuRow({
  icon,
  label,
  onPress,
}: {
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
}) {
  const c = useThemeColors();
  return (
    <AnimatedPressable
      onPress={() => { hapticLight(); onPress(); }}
      className="flex-row items-center gap-3 px-4 py-3.5 rounded-xl"
      style={{ backgroundColor: c.surface.primary }}
      accessibilityRole="button"
    >
      {icon}
      <Text style={{ color: c.text.primary }} className="text-sm font-medium flex-1" numberOfLines={1}>
        {label}
      </Text>
      <ChevronRight size={16} color={c.text.tertiary} />
    </AnimatedPressable>
  );
}

// ── Main screen ──

export default function AdminProfileScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { signOut, user } = useAuth();
  const profileUser = useProfileStore((s) => s.user);
  const { data: adminRole } = useAdminRole();
  const isDark = useThemeStore((s) => s.isDark);
  const toggleTheme = useThemeStore((s) => s.toggleDarkMode);
  const { confirmDestructive } = useAlert();

  const resetInverioStore = useInverioStore((s) => s.reset);
  const resetSubscriptionStore = useSubscriptionStore((s) => s.reset);
  const resetProfileStore = useProfileStore((s) => s.reset);

  const displayName = profileUser?.name || user?.user_metadata?.full_name || 'Admin';
  const displayEmail = profileUser?.email || user?.email || 'admin@inverio.ai';
  const avatarInitial = displayName.charAt(0).toUpperCase();

  const handleSignOut = async () => {
    const confirmed = await confirmDestructive(
      'Sign Out',
      'Are you sure you want to sign out?',
      'Sign Out'
    );
    if (!confirmed) return;
    try {
      track('sign_out');
      await signOut();
      resetInverioStore();
      resetSubscriptionStore();
      resetProfileStore();
    } catch (error) {
      reportError(error, { context: 'AdminProfile.handleSignOut' });
    } finally {
      if (isWeb) {
        window.location.replace('/login');
      } else {
        router.replace('/login');
      }
    }
  };

  const content = (
    <ScrollView
      className="flex-1"
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ paddingBottom: 40 }}
    >
      {/* ── Avatar + Identity ── */}
      <Animated.View entering={FadeInDown.duration(400)} className="items-center pt-8 pb-6 px-4">
        <View
          className="w-20 h-20 rounded-full items-center justify-center mb-3"
          style={{ backgroundColor: `${colors.primary[600]}15` }}
        >
          <Text style={{ color: colors.primary[600] }} className="text-3xl font-bold">
            {avatarInitial}
          </Text>
        </View>
        <Text style={{ color: colors.text.primary }} className="text-xl font-bold" numberOfLines={1}>
          {displayName}
        </Text>
        <View className="flex-row items-center gap-1.5 mt-1">
          <Mail size={14} color={colors.text.tertiary} />
          <Text style={{ color: colors.text.secondary }} className="text-sm" numberOfLines={1}>
            {displayEmail}
          </Text>
        </View>
        {adminRole && (
          <View
            className="flex-row items-center gap-1.5 mt-2 px-3 py-1 rounded-full"
            style={{ backgroundColor: `${colors.primary[600]}12` }}
          >
            <Shield size={14} color={colors.primary[600]} />
            <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold" numberOfLines={1}>
              {ADMIN_ROLE_LABELS[adminRole as keyof typeof ADMIN_ROLE_LABELS] ?? adminRole}
            </Text>
          </View>
        )}
      </Animated.View>

      {/* ── Edit Profile ── */}
      <Animated.View entering={FadeInDown.delay(80).duration(400)} className="px-4 mb-4">
        <AnimatedPressable
          onPress={() => { hapticLight(); router.push('/edit-profile'); }}
          className="flex-row items-center justify-center gap-2 py-3 rounded-xl"
          style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
          accessibilityRole="button"
        >
          <Pencil size={16} color={colors.primary[600]} />
          <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">
            Edit Profile
          </Text>
        </AnimatedPressable>
      </Animated.View>

      {/* ── Appearance ── */}
      <Animated.View entering={FadeInDown.delay(100).duration(400)} className="px-4 mb-4">
        <Text style={{ color: colors.text.tertiary }} className="text-xs font-semibold uppercase tracking-wide mb-2 ml-1">
          Appearance
        </Text>
        <AnimatedPressable
          onPress={() => { hapticLight(); toggleTheme(); }}
          className="flex-row items-center gap-3 px-4 py-3.5 rounded-xl"
          style={{ backgroundColor: colors.surface.primary }}
          accessibilityRole="button"
        >
          <Moon size={20} color={colors.text.secondary} />
          <Text style={{ color: colors.text.primary }} className="text-sm font-medium flex-1" numberOfLines={1}>
            Dark Mode
          </Text>
          <View
            className="w-10 h-6 rounded-full justify-center px-0.5"
            style={{ backgroundColor: isDark ? colors.primary[600] : colors.border.medium }}
          >
            <View
              className="w-5 h-5 rounded-full"
              style={{
                backgroundColor: '#FFFFFF',
                alignSelf: isDark ? 'flex-end' : 'flex-start',
              }}
            />
          </View>
        </AnimatedPressable>
      </Animated.View>

      {/* ── Account & Security ── */}
      <Animated.View entering={FadeInDown.delay(150).duration(400)} className="px-4 mb-4">
        <Text style={{ color: colors.text.tertiary }} className="text-xs font-semibold uppercase tracking-wide mb-2 ml-1">
          Account & Security
        </Text>
        <View className="gap-2">
          <MenuRow
            icon={<Lock size={20} color={colors.text.secondary} />}
            label="Change Password"
            onPress={() => router.push('/change-password')}
          />
          <MenuRow
            icon={<Shield size={20} color={colors.text.secondary} />}
            label="Security & MFA"
            onPress={() => router.push('/security')}
          />
          <MenuRow
            icon={<Bell size={20} color={colors.text.secondary} />}
            label="Notification Settings"
            onPress={() => router.push('/notification-settings')}
          />
        </View>
      </Animated.View>

      {/* ── About ── */}
      <Animated.View entering={FadeInDown.delay(200).duration(400)} className="px-4 mb-4">
        <Text style={{ color: colors.text.tertiary }} className="text-xs font-semibold uppercase tracking-wide mb-2 ml-1">
          About
        </Text>
        <View className="gap-2">
          <MenuRow
            icon={<HelpCircle size={20} color={colors.text.secondary} />}
            label="Help & Support"
            onPress={() => router.push('/help')}
          />
          <MenuRow
            icon={<FileText size={20} color={colors.text.secondary} />}
            label="Terms of Service"
            onPress={() => router.push('/terms')}
          />
          <MenuRow
            icon={<FileText size={20} color={colors.text.secondary} />}
            label="Privacy Policy"
            onPress={() => router.push('/privacy')}
          />
          <MenuRow
            icon={<Info size={20} color={colors.text.secondary} />}
            label="About Inverio"
            onPress={() => router.push('/about')}
          />
        </View>
      </Animated.View>

      {/* ── Sign Out ── */}
      <Animated.View entering={FadeInDown.delay(250).duration(400)} className="px-4 mt-2">
        <AnimatedPressable
          onPress={() => { hapticLight(); handleSignOut(); }}
          className="flex-row items-center justify-center gap-2 py-4 rounded-xl"
          style={{ backgroundColor: `${colors.error}10` }}
          accessibilityRole="button"
        >
          <LogOut size={20} color={colors.error} />
          <Text style={{ color: colors.error }} className="text-base font-semibold">
            Sign Out
          </Text>
        </AnimatedPressable>
      </Animated.View>
    </ScrollView>
  );

  if (isWeb) {
    return (
      <AppLayout title="Profile" breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'Profile' }]}>
        {content}
      </AppLayout>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['top', 'bottom']}>
      {content}
    </SafeAreaView>
  );
}
